import { Capacitor } from "@capacitor/core";
import appConfig, { channelEnum } from "../appconfig";

export default class MobileFriendlyCache {
	constructor(config = {}) {
		this.cache = new Map();
		this.defaultTTL = config.defaultCacheExpiryIn || 5 * 60 * 1000; // 5 minutes default
		this.expiryCheckInterval = config.expiryCheckInterval || 10 * 1000; // 10 seconds default
		this.autoDisposeAfterInactivity = config.autoDisposeAfterInactivity || (this.defaultTTL * 6); // Optional: auto-dispose after inactivity

		// Allow explicit disable
		if (config.autoDisposeAfterInactivity === null) {
			this.autoDisposeAfterInactivity = null;
		}

		// Detect if we're running on mobile (Capacitor environment)
		this.isMobile = this._detectMobile();

		// Track last access for inactivity-based disposal
		this.lastAccessTime = Date.now();

		// Track our specific cleanup handlers for removal
		this.cleanupHandlers = [];

		// AUTOMATIC: Register cleanup handlers (no manual call needed)
		this._registerCleanupHandler();

		// Strategy: Use background timer for web, lazy checking for mobile
		if (!this.isMobile) {
			this._startBackgroundCleanup();
		}

		this.disposed = false;
	}

	/**
	 * Detect if running on mobile (Capacitor environment)
	 * @returns {boolean} True if mobile environment
	 */
	_detectMobile() {
		// Native iOS/Android apps are always mobile
		if (Capacitor.isNativePlatform()) {
			return true;
		}

		// For web browsers, detect mobile channel
		if (appConfig.channelAlias() === channelEnum.mobileBrowser) {
			return true;
		}

		return false;
	}

	/**
	 * Start background cleanup timer for web platforms (race condition safe)
	 */
	_startBackgroundCleanup() {
		if (this.disposed) return;

		// Prevent multiple timers (race condition fix)
		if (this.cleanupTimer) {
			clearInterval(this.cleanupTimer);
		}

		this.cleanupTimer = setInterval(() => {
			if (this.disposed) return;

			// Regular cleanup of expired items
			this._cleanupExpiredItems();

			// Check for inactivity-based auto-disposal (critical for fire-and-forget scenarios)
			if (this.checkInactivityDisposal()) {
				return; // Cache was disposed, exit early
			}

			// Smart optimization: If cache is empty, pause timer to save resources
			if (this.cache.size === 0) {
				clearInterval(this.cleanupTimer);
				this.cleanupTimer = null;
			}

		}, this.expiryCheckInterval);
	}

	/**
	 * Clean up expired items (efficient implementation without millisecond iteration)
	 */
	_cleanupExpiredItems() {
		const now = Date.now();
		const expiredKeys = [];

		for (const [key, item] of this.cache.entries()) {
			if (item.expiryTime && now > item.expiryTime) {
				expiredKeys.push(key);
			}
		}

		expiredKeys.forEach(key => this.cache.delete(key));

		return expiredKeys.length;
	}

	/**
	 * Check for inactivity-based disposal (can be called externally for mobile)
	 * @returns {boolean} True if cache is disposed (either already or due to inactivity)
	 */
	checkInactivityDisposal() {
		// If already disposed, return true so calling code skips further operations
		if (this.disposed) return true;

		// If not configured for inactivity disposal, cache is not disposed
		if (!this.autoDisposeAfterInactivity) return false;

		const timeSinceLastAccess = Date.now() - this.lastAccessTime;
		if (timeSinceLastAccess > this.autoDisposeAfterInactivity) {
			this.dispose(); // This will properly stop all timers and clean up
			return true; // Was disposed due to inactivity
		}
		return false; // Not disposed
	}

	/**
	 * Store a value in cache with optional TTL
	 * @param {string} key - Cache key
	 * @param {*} value - Value to store
	 * @param {number} ttl - Time to live in milliseconds (optional)
	 * @returns {Object} Cache entry metadata (PureCache compatible)
	 */
	put(key, value, ttl = this.defaultTTL) {
		// If disposed due to inactivity, revive the cache since we're actively using it again
		if (this.disposed) {
			this._reviveCache();
		}

		// Track access time for inactivity-based disposal
		this._updateLastAccess();

		// Resume background cleanup if cache was empty (web only)
		this._resumeBackgroundCleanupIfNeeded();

		const addedAt = Date.now();
		const expiryTime = ttl ? addedAt + ttl : null;

		// Store complete structure internally for API compatibility
		this.cache.set(key, {
			value,
			addedAt,
			expiryTime
		});

		// Return PureCache-compatible structure
		return { value, addedAt, expiryAt: expiryTime };
	}

	/**
	 * Retrieve a value from cache
	 * @param {string} key - Cache key
	 * @returns {Object|null} Cache entry with {value, addedAt, expiryAt} structure or null if not found/expired (PureCache compatible)
	 */
	get(key) {
		// If disposed due to inactivity, revive the cache since we're actively using it again
		if (this.disposed) {
			this._reviveCache();
		}

		// Track access time for inactivity-based disposal
		this._updateLastAccess();

		const item = this.cache.get(key);
		if (!item) return null;

		// Always check expiry on access (both mobile and web)
		if (item.expiryTime && Date.now() > item.expiryTime) {
			this.cache.delete(key);
			return null;
		}

		// Return PureCache-compatible structure: {value, addedAt, expiryAt}
		return {
			value: item.value,
			addedAt: item.addedAt,
			expiryAt: item.expiryTime
		};
	}

	/**
	 * Update last access time for inactivity tracking (called by all access methods)
	 */
	_updateLastAccess() {
		this.lastAccessTime = Date.now();
	}

	/**
	 * Remove a specific key from cache
	 * @param {string} key - Cache key to remove
	 * @returns {boolean} True if key was removed
	 */
	remove(key) {
		if (this.disposed) return false; // Graceful failure instead of throwing
		this._updateLastAccess(); // Track access for inactivity
		return this.cache.delete(key);
	}

	/**
	 * Clear all cache entries
	 */
	clear() {
		if (this.disposed) return; // Graceful failure instead of throwing
		this._updateLastAccess(); // Track access for inactivity
		this.cache.clear();
	}

	/**
	 * Check if cache has a specific key (without triggering expiry check)
	 * @param {string} key - Cache key
	 * @returns {boolean} True if key exists in cache
	 */
	has(key) {
		if (this.disposed) return false; // Graceful failure instead of throwing
		this._updateLastAccess(); // Track access for inactivity
		return this.cache.has(key);
	}

	/**
	 * Get cache size
	 * @returns {number} Number of entries in cache
	 */
	size() {
		if (this.disposed) return 0; // Graceful failure instead of throwing
		this._updateLastAccess(); // Track access for inactivity
		return this.cache.size;
	}

	/**
	 * Manual cleanup of expired items (safe to call frequently)
	 * This is automatically called on web, but can be called manually on mobile
	 */
	cleanupExpired() {
		if (this.disposed) return 0; // Graceful failure instead of throwing
		this._updateLastAccess(); // Track access for inactivity
		return this._cleanupExpiredItems();
	}

	/**
	 * Get all cache keys (for debugging purposes)
	 * @returns {Array} Array of cache keys
	 */
	keys() {
		if (this.disposed) return []; // Graceful failure instead of throwing
		this._updateLastAccess(); // Track access for inactivity
		return Array.from(this.cache.keys());
	}

	/**
	 * Get cache statistics
	 * @returns {Object} Cache statistics
	 */
	stats() {
		if (this.disposed) {
			return {
				total: 0,
				active: 0,
				expired: 0,
				platform: this.isMobile ? "mobile" : "web",
				strategy: this.isMobile ? "lazy" : "background",
				disposed: true
			};
		}
		this._updateLastAccess(); // Track access for inactivity
		const now = Date.now();
		let expired = 0;
		let active = 0;

		for (const [, item] of this.cache.entries()) {
			if (item.expiryTime && now > item.expiryTime) {
				expired++;
			} else {
				active++;
			}
		}

		return {
			total: this.cache.size,
			active,
			expired,
			platform: this.isMobile ? "mobile" : "web",
			strategy: this.isMobile ? "lazy" : "background"
		};
	}

	/**
	 * Automatically register cleanup handlers on window unload (private)
	 * No manual call needed - this is done automatically in constructor
	 */
	_registerCleanupHandler() {
		if (typeof window === "undefined" || this.disposed) return;

		const handler = () => {
			this.dispose();
		};

		window.addEventListener("beforeunload", handler);

		// Track so we can remove later
		this.cleanupHandlers.push(
			{ event: "beforeunload", handler }
		);
	}

	/**
	 * Remove all registered cleanup handlers
	 */
	_removeCleanupHandlers() {
		if (typeof window === "undefined") return;

		this.cleanupHandlers.forEach(({ event, handler }) => {
			window.removeEventListener(event, handler);
		});
		this.cleanupHandlers = [];
	}

	/**
	 * Dispose of the cache instance and clean up resources
	 */
	dispose() {
		if (this.disposed) return; // Already disposed

		// Clear background timer if it exists
		if (this.cleanupTimer) {
			clearInterval(this.cleanupTimer);
			this.cleanupTimer = null;
		}

		// Remove window event listeners to prevent accumulation
		this._removeCleanupHandlers();

		// Clear cache data
		this.cache.clear();
		this.disposed = true;
	}

	/**
	 * Revive a disposed cache when it's actively used again
	 */
	_reviveCache() {
		if (!this.disposed) return; // Already active

		// Mark as active again
		this.disposed = false;

		// Re-register cleanup handlers
		this._registerCleanupHandler();

		// Restart background cleanup for web platforms
		if (!this.isMobile) {
			this._startBackgroundCleanup();
		}

		// Update last access time
		this.lastAccessTime = Date.now();
	}

	/**
	 * Resume background cleanup if it was paused due to empty cache
	 */
	_resumeBackgroundCleanupIfNeeded() {
		// Only for web platforms
		if (this.isMobile || this.disposed) return;

		// Resume timer if it was paused and we now have data
		if (!this.cleanupTimer && this.cache.size > 0) {
			this._startBackgroundCleanup();
		}
	}
}
